import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import SubscriptionPage from './pages/SubscriptionPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import DashboardPage from './pages/DashboardPage';
import SearchOperatorsPage from './pages/SearchOperatorsPage';
import OperatorsPage from './pages/OperatorsPage';
import ReportsPage from './pages/ReportsPage';
import LicenseManagementPage from './pages/LicenseManagementPage';
import InventoryManagementPage from './pages/InventoryManagementPage';
import LabCoaManagementPage from './pages/LabCoaManagementPage';
import ComplianceReportsPage from './pages/ComplianceReportsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import SettingsPage from './pages/SettingsPage';
import { Toaster } from './components/ui/sonner';
import subscriptionService from './services/subscriptionService';
import authService from './services/authService';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    checkAuthAndSubscription();
  }, []);

  const checkAuthAndSubscription = async () => {
    setIsCheckingAuth(true);
    
    // Check if user is authenticated
    const token = localStorage.getItem('authToken');
    const isAuth = !!token;
    setIsAuthenticated(isAuth);

    if (isAuth) {
      // User is logged in, check subscription status
      try {
        console.log('Checking subscription status for authenticated user...');
        const subscriptionStatus = await subscriptionService.checkSubscriptionStatus();
        console.log('Subscription status:', subscriptionStatus);
        
        // If subscription is not active and user is not already on subscription/payment pages
        if (subscriptionStatus.status !== 'ACTIVE') {
          const currentPath = window.location.pathname;
          console.log('Subscription not active. Current path:', currentPath);
          
          // Only redirect if not already on subscription/payment pages
          if (!currentPath.includes('/subscription') && 
              !currentPath.includes('/payment-success') && 
              !currentPath.includes('/payment-cancel') &&
              !currentPath.includes('/login') &&
              !currentPath.includes('/register') &&
              !currentPath.includes('/forgot-password') &&
              currentPath !== '/') {
            
            console.log('Redirecting to subscription page...');
            // Redirect to subscription page
            window.location.href = '/subscription';
          }
        } else {
          console.log('Subscription is ACTIVE, allowing access');
        }
      } catch (error) {
        console.error('Failed to check subscription status:', error);
      }
    }
    
    setIsCheckingAuth(false);
  };

  const ProtectedRoute = ({ children }) => {
    const [hasAccess, setHasAccess] = useState(null);

    useEffect(() => {
      const checkAccess = async () => {
        console.log('ProtectedRoute: Checking access...', 'isAuthenticated:', isAuthenticated);
        
        if (!isAuthenticated) {
          console.log('ProtectedRoute: Not authenticated, denying access');
          setHasAccess(false);
          return;
        }

        try {
          console.log('ProtectedRoute: Checking subscription status...');
          
          // Set a safety timeout to prevent hanging
          const timeoutId = setTimeout(() => {
            console.warn('ProtectedRoute: Subscription check taking too long, denying access');
            setHasAccess(false);
          }, 12000); // 12 second safety net
          
          const subscriptionStatus = await subscriptionService.checkSubscriptionStatus();
          
          // Clear the timeout since we got a response
          clearTimeout(timeoutId);
          
          console.log('ProtectedRoute: Subscription status:', subscriptionStatus);
          
          if (subscriptionStatus.status === 'ACTIVE') {
            console.log('ProtectedRoute: Access granted (ACTIVE subscription)');
            setHasAccess(true);
          } else {
            console.log('ProtectedRoute: Access denied (subscription not ACTIVE)');
            setHasAccess(false);
          }
        } catch (error) {
          console.error('ProtectedRoute: Subscription check failed:', error);
          setHasAccess(false);
        }
      };

      checkAccess();
    }, []);

    if (hasAccess === null) {
      // Still checking
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Checking subscription...</p>
          </div>
        </div>
      );
    }

    if (!isAuthenticated) {
      return <Navigate to="/login" />;
    }

    if (!hasAccess) {
      return <Navigate to="/subscription" />;
    }

    return children;
  };

  // Show loading while checking initial auth
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage setIsAuthenticated={setIsAuthenticated} />} />
          <Route path="/register" element={<RegisterPage setIsAuthenticated={setIsAuthenticated} />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/payment-cancel" element={<PaymentCancelPage />} />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/search-operators" element={
            <ProtectedRoute>
              <SearchOperatorsPage />
            </ProtectedRoute>
          } />
          <Route path="/operators" element={
            <ProtectedRoute>
              <OperatorsPage />
            </ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute>
              <ReportsPage />
            </ProtectedRoute>
          } />
          <Route path="/licenses" element={
            <ProtectedRoute>
              <LicenseManagementPage />
            </ProtectedRoute>
          } />
          <Route path="/inventory" element={
            <ProtectedRoute>
              <InventoryManagementPage />
            </ProtectedRoute>
          } />
          <Route path="/lab-coa" element={
            <ProtectedRoute>
              <LabCoaManagementPage />
            </ProtectedRoute>
          } />
          <Route path="/compliance-reports" element={
            <ProtectedRoute>
              <ComplianceReportsPage />
            </ProtectedRoute>
          } />
          <Route path="/analytics" element={
            <ProtectedRoute>
              <AnalyticsPage />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;